import { clearAccessTokenExpiry, getAccessTokenExpiry } from '~/lib/api/access-token-expiry';
import { refreshSession } from '~/lib/api/client';

/**
 * Proactive half of token rotation: refresh the pair a minute before the access
 * token expires, so an idle-but-open tab never actually hits a 401.
 *
 * `~/lib/api/client` covers the reactive half. This module is the optimisation;
 * that one is the guarantee.
 */

/** Refresh this long before expiry, to absorb clock skew and a slow round trip. */
const REFRESH_SKEW_MS = 60_000;

/** Never busy-loop, even if the recorded expiry is nonsense. */
const MIN_DELAY_MS = 2_000;

/** setTimeout silently fires immediately beyond this, so re-arm in chunks instead. */
const MAX_DELAY_MS = 2_147_483_647;

let timer: ReturnType<typeof setTimeout> | null = null;
let running = false;

function clearTimer(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
}

function schedule(): void {
  clearTimer();
  if (!running) return;

  const expiresAt = getAccessTokenExpiry();

  // Nothing recorded — the session was restored from a cookie this tab never
  // saw issued. Reactive refresh will cover it, and the next successful refresh
  // records an expiry that puts us back on schedule.
  if (expiresAt === null) return;

  const untilRefresh = expiresAt - Date.now() - REFRESH_SKEW_MS;

  if (untilRefresh > MAX_DELAY_MS) {
    timer = setTimeout(schedule, MAX_DELAY_MS);
    return;
  }

  timer = setTimeout(
    () => {
      void rotate();
    },
    Math.max(untilRefresh, MIN_DELAY_MS),
  );
}

async function rotate(): Promise<void> {
  if (!running) return;

  const ok = await refreshSession();

  // On failure the client has already emitted session-expired, and the auth
  // provider stops rotation in response. Re-scheduling would only spin.
  if (ok) schedule();
}

/**
 * A tab that was backgrounded or a laptop that slept can leave the timer
 * overdue by minutes. Re-check whenever the tab becomes visible again.
 */
function handleVisibilityChange(): void {
  if (document.visibilityState !== 'visible' || !running) return;

  const expiresAt = getAccessTokenExpiry();
  if (expiresAt !== null && expiresAt - Date.now() <= REFRESH_SKEW_MS) {
    void rotate();
  } else {
    schedule();
  }
}

/** Idempotent. Called when a session becomes active. */
export function startTokenRotation(): void {
  if (running || typeof window === 'undefined') return;

  running = true;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  schedule();
}

/** Idempotent. Called on logout and on session expiry. */
export function stopTokenRotation(): void {
  if (!running) return;

  running = false;
  clearTimer();
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  clearAccessTokenExpiry();
}
